import type { AiReprocessRun } from "@/shared/api/api-client";
import {
  isAiReprocessRunning,
  progressEtaLabel,
  progressSegments
} from "@/features/mentions/ai-reprocess-view";

/**
 * A barra de andamento do reprocessamento por IA — a mesma na amostra
 * (dentro do diálogo) e na aplicação (no banner da tela de menções).
 *
 * A barra mede só o andamento, numa cor; o desfecho de cada menção já
 * processada (alterada, sem mudança...) fica na legenda embaixo, só em
 * texto. Na fila (`QUEUED`, ou sem total ainda) ela anda sozinha, sem
 * número: a pessoa vê que algo está acontecendo sem ler um "0 de 300"
 * parado, que parece travado.
 */
export function AiReprocessProgress({
  run,
  label
}: {
  run: AiReprocessRun;
  /** O que está andando — "Reprocessamento", "Amostra". */
  label: string;
}) {
  const { total, pending } = run.counters;
  const done = Math.max(total - pending, 0);
  const running = isAiReprocessRunning(run) || run.retrying === true;
  const waiting = running && (run.status === "QUEUED" || total === 0);
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const segments = progressSegments(run);
  const eta = running && !waiting ? progressEtaLabel(run, Date.now()) : null;

  return (
    <div className={`ai-progress${running ? " is-running" : ""}`}>
      <div
        className={`ai-progress-track${waiting ? " is-waiting" : ""}`}
        role="progressbar"
        aria-label={`Andamento: ${label}`}
        aria-valuemin={0}
        aria-valuemax={total || undefined}
        aria-valuenow={waiting ? undefined : done}
        aria-valuetext={
          waiting
            ? "Na fila"
            : `${done.toLocaleString("pt-BR")} de ${total.toLocaleString("pt-BR")} (${percent}%)`
        }
      >
        {!waiting && (
          <span className="ai-progress-fill" style={{ width: `${(done / total) * 100}%` }} />
        )}
      </div>
      <div className="ai-progress-meta">
        <span className="ai-progress-count">
          {waiting ? (
            "Na fila — começa em instantes"
          ) : (
            <>
              <strong>{percent}%</strong> · {done.toLocaleString("pt-BR")} de{" "}
              {total.toLocaleString("pt-BR")} {total === 1 ? "menção" : "menções"}
            </>
          )}
        </span>
        {eta && <span className="ai-progress-eta">{eta}</span>}
      </div>
      {segments.length > 0 && (
        <ul className="ai-progress-legend" aria-label="Resultado até aqui">
          {segments.map(segment => (
            <li key={segment.key}>
              {segment.count.toLocaleString("pt-BR")} {segment.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
